import { KeyboardEvent, ReactNode, useRef } from 'react';
import { keyframes, Paper, Stack, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

import Tag from 'modules/common/components/MUI/Tag';
import CheckCircleFilledIcon from 'modules/common/components/Icons/CheckCircleFilledIcon';
import useDeepLinkHighlight, { HIGHLIGHT_DURATION_MS } from 'modules/common/hooks/useDeepLinkHighlight';

// Spacing units a row's background spills horizontally past the content edge on hover / while
// completing. Exported so the feed's collapse wrapper can widen its clip box to match (otherwise
// the Collapse's overflow:hidden crops the spill mid-animation).
export const ROW_SPILL = 1.5;

// Deep-link flash fade-out portion at the tail of the flash. A dedicated animation (not the row's 0.15s
// hover transition) lets the fade run at the same 600ms the Transactions/Documents deep-link uses without
// making the snappy hover affordance sluggish. The flash's total lifetime is HIGHLIGHT_DURATION_MS
// (imported from the hook that owns the flash state) so the two can't drift and the animation always
// finishes at-or-before the flash state clears (no snap).
const DEEP_LINK_FADE_MS = 600;

const TaskRowSurface = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'completing' && prop !== 'completed' && prop !== 'highlighted',
})<{ completing?: boolean; completed?: boolean; highlighted?: boolean }>(
    ({ theme, completing, completed, highlighted }) => {
        // Horizontal spillover past the content edge (margin) with compensating padding, plus a z-index
        // that stacks the row above its sibling dividers (the box-shadow ring covers them). Shared by the
        // hover affordance and the completion flash so the green fills the same width as a hovered row.
        const spill = {
            margin: theme.spacing(0, -ROW_SPILL),
            padding: theme.spacing(ROW_SPILL),
            position: 'relative' as const,
            zIndex: 1,
        };

        // Hold the deep-link tint, then fade it (background + ring) to transparent over the final
        // DEEP_LINK_FADE_MS. The spill margin + padding cancel out for content position, so removing them
        // when the flash ends doesn't shift the row — only the (now-transparent) background box changes width.
        const holdPct = ((HIGHLIGHT_DURATION_MS - DEEP_LINK_FADE_MS) / HIGHLIGHT_DURATION_MS) * 100;
        const deepLinkFlash = keyframes`
            0%, ${holdPct}% {
                background-color: ${theme.palette.shades.success['12p']};
                box-shadow: 0 0 0 1px ${theme.palette.shades.success['12p']};
            }
            100% {
                background-color: transparent;
                box-shadow: 0 0 0 1px transparent;
            }
        `;

        return {
            padding: theme.spacing(1.5, 0),
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(3),
            cursor: completed || completing ? 'default' : 'pointer',
            transition: 'background-color 0.15s, margin 0.15s, padding 0.15s',
            // Interactive (active, not mid-exit) rows get the hover affordance.
            ...(!completed &&
                !completing && {
                    '&:hover, &:focus-within': {
                        backgroundColor: theme.palette.cement[50],
                        ...spill,
                        boxShadow: `0 0 0 1px ${theme.palette.cement[50]}`,
                        '& .MuiPaper-root': {
                            border: 'none',
                            boxShadow: theme.shadows[9],
                        },
                    },
                }),
            // Exit-animation flash (green[100] === success.lightHover). Handled here, not via sx, so it
            // out-specifies the :focus-within rule — focus returns to the row when its modal closes.
            ...(completing && {
                backgroundColor: theme.palette.green[100],
                ...spill,
                boxShadow: `0 0 0 1px ${theme.palette.green[100]}`,
                '&:hover, &:focus-within': { backgroundColor: theme.palette.green[100] },
            }),
            // Deep-link flash: the same success tint the Documents/Transactions deep-links use. Spills +
            // rings like the hover/completing states so the tint fills the same width — without the spill
            // it stops at the content edge and reads as narrower/misaligned next to a hovered row. Only
            // for an active row (a completing/completed row owns its own green treatment above).
            ...(highlighted &&
                !completed &&
                !completing && {
                    backgroundColor: theme.palette.shades.success['12p'],
                    ...spill,
                    boxShadow: `0 0 0 1px ${theme.palette.shades.success['12p']}`,
                    // Fade the tint out over the flash lifetime. Under reduced-motion, drop the animation
                    // (matching how the codebase's other animated components honor the preference); the
                    // static tint + ring above just show, then clear when the flash state resets.
                    animation: `${deepLinkFlash} ${HIGHLIGHT_DURATION_MS}ms ease-out both`,
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }),
        };
    }
);

interface TaskRowShellProps {
    title: string;
    onCtaClick: () => void;
    dateBadge?: ReactNode;
    description?: string;
    overdue?: boolean;
    /** Persisted completed row (group panel): green "Completed" label + check, non-interactive. */
    completed?: boolean;
    /** Feed exit animation is playing: flash the surface green before it collapses away. */
    isCompleting?: boolean;
    /** This row is the deep-link target: scroll it into view and flash it once on mount. */
    isDeepLinkTarget?: boolean;
}

/**
 * Presentational row layout shared by TaskRow and TaskGroupRow: a clickable surface with a
 * date badge, title/description, and a right-justified "Overdue" tag. The row is the only
 * control — the whole surface activates the CTA (mouse + keyboard), so there's no separate
 * button; each consumer supplies its own data and click behavior.
 */
export default function TaskRowShell({
    title,
    onCtaClick,
    dateBadge,
    description,
    overdue = false,
    completed = false,
    isCompleting = false,
    isDeepLinkTarget = false,
}: Readonly<TaskRowShellProps>) {
    const theme = useTheme();
    const surfaceRef = useRef<HTMLDivElement>(null);
    // Instant scroll: the Tasks page strips the deep-link param once consumed, and that re-render
    // cancels an in-flight smooth scroll before it reaches the row.
    const highlighted = useDeepLinkHighlight(isDeepLinkTarget, surfaceRef, 'auto');
    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.currentTarget !== e.target) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCtaClick();
        }
    };

    // A completed row is a static summary; a completing row is mid-exit-animation. Neither is
    // interactive — only active rows are clickable. (TransitionGroup keeps the completing row mounted
    // with its old props during the exit, so without this a click would re-fire onCtaClick.)
    const interactiveProps =
        completed || isCompleting ? {} : { role: 'button', tabIndex: 0, onClick: onCtaClick, onKeyDown: handleKeyDown };

    return (
        <TaskRowSurface
            ref={surfaceRef}
            completed={completed}
            completing={isCompleting}
            highlighted={highlighted}
            {...interactiveProps}>
            {dateBadge}
            <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">{title}</Typography>
                    {overdue && !completed && <Tag label="Overdue" color="error" variant="filled" />}
                </Stack>
                {completed ? (
                    <Typography variant="body2" color="success.main">
                        Completed
                    </Typography>
                ) : (
                    description && (
                        <Typography variant="body2" color="text.secondary">
                            {description}
                        </Typography>
                    )
                )}
            </Stack>
            {completed && <CheckCircleFilledIcon fill={theme.palette.success.main} />}
        </TaskRowSurface>
    );
}
